import type {
  ConversationRead,
  ConversationSummary,
  FollowedThread,
  MessageView,
  ShownMessage,
} from "@beacon/core/chat"
import { describeRelativeTime } from "@/time/describe.ts"
import { describeReplies, placeOf } from "./describe.ts"
import { readingFromHref } from "./message-place.ts"
import { authorWords, saidBy } from "./authors.ts"

/*
 * What is unread, as the web app shows it. The web API keeps the Member's read state and counts
 * what is unread from the record (`markRead`, `unreadsQuery`); these say it, place it and link to
 * it, and decide when a screen asks to move it.
 */

/** How many messages are unread, as words read after a name; nothing when none. */
export const describeUnread = (count: number) =>
  count <= 0 ? "" : count === 1 ? "1 unread message" : `${count} unread messages`

/** How many replies in a followed thread are new, as words; nothing when none. */
export const describeNewReplies = (count: number) =>
  count <= 0 ? "" : count === 1 ? "1 new reply" : `${count} new replies`

/** How many followed threads hold new replies, as words; nothing when none. */
export const describeThreadsUnread = (count: number) =>
  count <= 0 ? "" : count === 1 ? "1 thread with new replies" : `${count} threads with new replies`

/** A count no wider than a badge holds: up to 99, then "99+". */
export const unreadCountShown = (count: number) => (count > 99 ? "99+" : String(count))

/**
 * Where a conversation in the Member's list opens: reading on from its first unread message, so
 * they read on from where they left off, or as it opens (at its newest) when nothing is unread.
 */
export const conversationHref = ({ id, unread, firstUnread }: ConversationSummary) =>
  unread > 0 && firstUnread ? readingFromHref(id, firstUnread) : `/chat/${id}`

/** Where a followed thread opens: the thread itself, beside its conversation. */
export const threadHref = ({ conversation, root }: FollowedThread) =>
  `/chat/${conversation.id}?${new URLSearchParams({ thread: root.id }).toString()}`

/**
 * The message the New line stands before: the first that someone else said past `readSeq`, where
 * the Member had read the conversation to as it opened. None when nothing is new, or while they
 * read it without joining (no `readSeq`), which tracks nothing.
 */
export function unreadDividerOf(
  messages: readonly ShownMessage[],
  readSeq: number | null,
  me: string,
): string | null {
  if (readSeq === null) return null
  // A deleted message's placeholder is nobody's, and never unread.
  return (
    messages.find(
      (message) => message.seq > readSeq && message.author !== null && !saidBy(message, me),
    )?.id ?? null
  )
}

/**
 * The change to mark read up to, now the Member has seen everything up to `cursor`: none when the
 * web API already has them there (`readSeq`) or has already been asked (`asked`), so a screen asks
 * once for each place it reaches, and only ever forward.
 */
export function readMarkOf({
  cursor,
  readSeq,
  asked,
}: {
  cursor: number
  readSeq: number | null
  asked: number
}): number | undefined {
  return cursor > Math.max(readSeq ?? 0, asked) ? cursor : undefined
}

/** The Member's list with one conversation's read state as the web API now answers it. */
export function withRead(
  list: readonly ConversationSummary[] | undefined,
  { conversationId, readSeq, unread, firstUnread }: ConversationRead,
): ConversationSummary[] | undefined {
  return list?.map((conversation) =>
    conversation.id === conversationId
      ? { ...conversation, readSeq, unread, firstUnread }
      : conversation,
  )
}

/**
 * A followed thread as its row names it to Member `me`: who started it ("You" for them) and what
 * they said, as one line (a message of files alone, by how many); one whose message was deleted
 * says only that.
 */
export function threadLineOf({ root }: FollowedThread, me: string) {
  if (root.deleted) return "This message was deleted."
  const said =
    plainLine(root.body) ||
    (root.files.length === 1 ? "Shared a file" : `Shared ${root.files.length} files`)
  return `${saidBy(root, me) ? "You" : authorWords(root.author)}: ${said}`
}

/** Where a followed thread is and how it stands: "#general · 3 replies · 5 minutes ago". */
export function describeFollowedThread({ conversation, root }: FollowedThread, now: Date) {
  return [
    placeOf(conversation),
    describeReplies(root.thread?.replyCount ?? 0),
    root.thread?.lastReplyAt ? describeRelativeTime(root.thread.lastReplyAt, now) : undefined,
  ]
    .filter(Boolean)
    .join(" · ")
}

/** How long a message's one line runs before it is cut short. */
const lineLength = 140

/**
 * A message's markdown as one line of plain words, for a list that names it: fences, quotes,
 * list marks, emphasis and code marks dropped, a link or a mention by its words, an attachment by
 * its name, cut short at `lineLength`.
 */
export function plainLine(markdown: string) {
  const words = markdown
    .replace(/```[^\n]*\n?/g, " ")
    .replace(/!?\[([^\]]*)\]\([^)]*\)/g, "$1")
    .replace(/^\s{0,3}(?:>|[*+-]|\d+[.)]|#{1,6})\s+/gm, "")
    .replace(/(\*\*|__|~~|`)/g, "")
    .replace(/(^|[\s(])[*_]([^*_\n]+)[*_](?=[\s).,!?:;]|$)/g, "$1$2")
    .replace(/\s+/g, " ")
    .trim()
  return words.length > lineLength ? `${words.slice(0, lineLength - 1)}…` : words
}
